/**
 * useApplicationCompletion — the contestant adapter for the shared
 * Completion_Service (application-completion-tracker spec, design §C2).
 *
 * The `/apply` `ApplicationFormContainer` already holds every input the pure
 * `computeCompletion` needs — the active FormVersion snapshot, the live React
 * Hook Form values, the applicant's division, and the Document_Catalog +
 * uploads (via `useApplyDocumentSlots`). This hook subscribes to the container's
 * form via `form.watch()` and recomputes the Completion_Result on every value
 * change, so the CompletionBar tracks edits live (Requirement 7.4). It adds NO
 * new fetch on this surface.
 *
 * On any thrown error it soft-fails to an UNAVAILABLE result so the surrounding
 * page always renders (Requirement 8.2, 8.3). The pure function is itself total,
 * so the try/catch is defence-in-depth per the design's Error Handling section.
 *
 * Validates: Requirements 1.3, 7.4, 8.2, 8.3
 */

import * as React from 'react';
import type { UseFormReturn } from 'react-hook-form';

import type { ApplicationResponseValues } from '@/lib/application-form/deriveZodSchema';
import {
  computeCompletion,
  type CompletionInput,
  type CompletionResult,
} from '@/lib/application-completion/computeCompletion';

/** The already-held inputs the container passes through (no new fetch). */
export interface UseApplicationCompletionArgs {
  /** The container's React Hook Form; watched so the bar updates on edits. */
  form: UseFormReturn<ApplicationResponseValues>;
  /** Everything `computeCompletion` needs except the live `values`. */
  snapshot: CompletionInput['snapshot'];
  divisionKey: CompletionInput['divisionKey'];
  documentCatalog: CompletionInput['documentCatalog'];
  uploadedDocuments: CompletionInput['uploadedDocuments'];
}

const UNAVAILABLE: CompletionResult = {
  percent: 0,
  answeredCount: 0,
  totalCount: 0,
  availability: 'UNAVAILABLE',
};

/**
 * Compute the applicant's live Completion_Result. Recomputes whenever the
 * watched form values, snapshot, division, catalog, or uploads change.
 */
export function useApplicationCompletion({
  form,
  snapshot,
  divisionKey,
  documentCatalog,
  uploadedDocuments,
}: UseApplicationCompletionArgs): CompletionResult {
  // Subscribe to the live form values so edits re-render the bar (Requirement
  // 7.4). `form.watch()` returns the current values object and re-runs this
  // component on every change.
  const watchedValues = form.watch();

  return React.useMemo<CompletionResult>(() => {
    try {
      return computeCompletion({
        snapshot,
        values: (watchedValues ?? {}) as CompletionInput['values'],
        divisionKey,
        documentCatalog,
        uploadedDocuments,
      });
    } catch {
      // Soft-fail: a missing metric never breaks the surface (Requirement 8.3).
      return UNAVAILABLE;
    }
  }, [snapshot, watchedValues, divisionKey, documentCatalog, uploadedDocuments]);
}

export default useApplicationCompletion;
